import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { setIdentityUser } from '../api/client.js';

/**
 * Auth context — a single Netlify Identity session shared across the whole tree.
 * AuthProvider must wrap the app root; useAuth() consumes the context.
 */
const AuthContext = createContext(null);

/**
 * Dev-only mock auth, for testing authenticated UI (dashboard, admin pages,
 * modals, forms) locally without a real Netlify Identity login.
 *
 * `import.meta.env.DEV` is a compile-time constant Vite replaces with the
 * literal `false` in production builds — everything inside this branch is
 * dead-code-eliminated from the production bundle, not just disabled by a
 * runtime check. It also requires an explicit `?mockAuth=admin` (or
 * `?mockAuth=member`) query param, so ordinary `npm run dev` against the
 * real widget is unaffected.
 *
 * Use this to actually click through a fix locally before calling it done,
 * instead of reasoning from source alone — most UI bugs (e.g. a modal
 * stealing focus on every keystroke) don't need a real backend to reproduce.
 */
function getMockUser() {
  if (!import.meta.env.DEV) return null;
  const mode = new URLSearchParams(window.location.search).get('mockAuth');
  if (!mode) return null;
  return {
    sub: 'mock-user-1',
    id: 'mock-user-1',
    email: mode === 'admin' ? 'mock-admin@example.test' : 'mock-member@example.test',
    app_metadata: { roles: mode === 'admin' ? ['admin'] : [], orgId: 'default' },
    jwt: async () => 'mock-jwt-token',
  };
}

export function AuthProvider({ children }) {
  const mockUser = getMockUser();
  const [user, setUser] = useState(mockUser);
  const [loading, setLoading] = useState(!mockUser);
  // True while a password-recovery or invite flow is in progress.
  // LoginPage must NOT redirect to dashboard during this window — the
  // Identity widget needs to show its "set new password" dialog first.
  const [isRecovery, setIsRecovery] = useState(false);

  useEffect(() => {
    if (mockUser) {
      setIdentityUser(mockUser);
      return;
    }
    const ni = window.netlifyIdentity;
    if (!ni) {
      console.error('Netlify Identity widget not loaded');
      setLoading(false);
      return;
    }

    // Restore existing session synchronously before any async init
    const current = ni.currentUser();
    if (current) {
      setUser(current);
      setIdentityUser(current);
    }
    setLoading(false);

    const onLogin = (u) => {
      setIsRecovery(false);
      setUser(u);
      setIdentityUser(u);
      ni.close();
    };
    const onLogout = () => {
      setUser(null);
      setIdentityUser(null);
    };
    const onInit = (u) => {
      if (u) {
        setUser(u);
        setIdentityUser(u);
      }
      setLoading(false);
    };
    // Fired when the URL contains a recovery_token (password reset).
    const onRecovery = () => setIsRecovery(true);

    ni.on('login', onLogin);
    ni.on('logout', onLogout);
    ni.on('init', onInit);
    ni.on('passwordRecovery', onRecovery);
    // Do NOT call ni.init() here. The widget's own bundle already calls its
    // internal init() automatically (on DOMContentLoaded, or immediately if
    // the document has already loaded) — see the widget source: the code
    // that reads location.hash for invite_token/recovery_token and opens the
    // modal (function `m()`, invoked from the modal iframe's onload handler)
    // only runs once per init() call, and each init() call creates a NEW
    // modal iframe. The shared modal-visibility state lives on one singleton
    // store, and the reactive style-binding that shows/hides the iframe only
    // fires on a value CHANGE — it always targets whichever iframe was
    // created most recently. Calling ni.init() a second time here creates a
    // second iframe; if the widget's own (first) auto-init already flipped
    // modal.isOpen to true while processing an invite/recovery token, our
    // second iframe never receives that isOpen transition and stays
    // invisible — the page looks like nothing happened (goes to the login
    // route with no modal). This was the actual cause of that bug: a race
    // between two init() calls, not anything wrong with the token or route.
    // Registering listeners (above) is sufficient — currentUser() below
    // covers an already-restored session, and the widget's own auto-init
    // still fires our 'login'/'init'/'passwordRecovery' listeners normally.

    return () => {
      ni.off('login', onLogin);
      ni.off('logout', onLogout);
      ni.off('init', onInit);
      ni.off('passwordRecovery', onRecovery);
    };
  }, []);

  const login = useCallback(() => {
    window.netlifyIdentity?.open('login');
  }, []);

  const logout = useCallback(() => {
    window.netlifyIdentity?.logout();
  }, []);

  const isAdmin = user?.app_metadata?.roles?.includes('admin') ?? false;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, isRecovery }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth() — consumes the shared AuthContext.
 * All components (AppShell, RequireAuth, LoginPage, etc.) share the same
 * user object so there is never a moment where one has user=null while
 * another has user=<logged-in user>.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
